/**
 * Infinite Scroll for Shopify Collection Pages
 * HTML-based approach - works on any Shopify store
 */
class InfiniteScroll {
  constructor() {
    this.container = document.querySelector('[data-product-column]');
    this.productGridContainer = document.getElementById('ProductGridContainer');
    this.pagination = document.querySelector('.pagination');
    
    this.isLoading = false;
    this.hasMorePages = true;
    this.currentPage = 1;
    
    // RESPONSIVE SCROLL TRIGGER DISTANCES
    this.triggerDistance = this.getTriggerDistance();
    
    if (this.container && this.productGridContainer) {
      this.init();
    } else {
      this.showPagination();
    }
  }

  init() {
    this.updateSettings();
    this.createLoadingIndicator();
    this.bindScrollEvent();
    this.bindFacetEvents();
    this.checkForMorePages();
    
    if (this.hasMorePages) {
      this.hidePagination();
    }
  }

  getTriggerDistance() {
    // Responsive trigger distances based on screen size
    const screenWidth = window.innerWidth;
    
    if (screenWidth >= 1200) {
      return 2200; // Large desktop - very early loading
    } else if (screenWidth >= 768) {
      return 2200; // Desktop/tablet - early loading  
    } else {
      return 2200;  // Mobile - moderate loading
    }
  }

  updateSettings() {
    const urlParams = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(urlParams.get('page')) || 1;
  }

  checkForMorePages() {
    if (!this.pagination) {
      this.hasMorePages = false;
      return;
    }

    const allPageLinks = this.pagination.querySelectorAll('a.pagination__item.link');
    const currentPageElement = this.pagination.querySelector('.pagination__item--current');
    const currentPageNum = parseInt(currentPageElement?.textContent?.trim()) || this.currentPage;
    
    this.hasMorePages = Array.from(allPageLinks).some(link => {
      const linkText = link.textContent?.trim();
      const pageNum = parseInt(linkText);
      return pageNum && pageNum > currentPageNum;
    });
  }

  hidePagination() {
    if (this.pagination) {
      this.pagination.style.display = 'none';
    }
  }

  showPagination() {
    if (this.pagination) {
      this.pagination.style.display = '';
    }
  }

  createLoadingIndicator() {
    this.loadingIndicator = document.createElement('div');
    this.loadingIndicator.className = 'infinite-scroll-loading';
    this.loadingIndicator.innerHTML = `
      <div class="infinite-scroll-spinner">
        <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
        <span>Loading more products...</span>
      </div>
    `;
    
    this.loadingIndicator.style.cssText = `
      text-align: center;
      padding: 40px 20px;
      display: none;
    `;
    
    if (!document.getElementById('infinite-scroll-styles')) {
      const style = document.createElement('style');
      style.id = 'infinite-scroll-styles';
      style.textContent = `
        .infinite-scroll-spinner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: rgba(var(--color-foreground), 0.7);
        }
        .infinite-scroll-loading .spinner {
          width: 20px;
          height: 20px;
          animation: rotator 1.4s linear infinite;
        }
        .infinite-scroll-loading .path {
          stroke-dasharray: 90, 150;
          stroke-dashoffset: 0;
          stroke-linecap: round;
          stroke: rgba(var(--color-foreground), 0.7);
          animation: dash 1.5s ease-in-out infinite;
        }
        @keyframes rotator {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(270deg); }
        }
        @keyframes dash {
          0% { stroke-dashoffset: 0; }
          50% { stroke-dashoffset: -35; stroke-dasharray: 90, 150; }
          100% { stroke-dashoffset: -124; }
        }
      `;
      document.head.appendChild(style);
    }
    
    this.productGridContainer.appendChild(this.loadingIndicator);
  }

  bindScrollEvent() {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.checkScrollPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Update trigger distance on window resize
    const handleResize = () => {
      this.triggerDistance = this.getTriggerDistance();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
  }

  bindFacetEvents() {
    document.addEventListener('facets:updated', () => {
      this.resetScroll();
    });

    const sortSelect = document.getElementById('SortBy');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        this.resetScroll();
      });
    }
  }

  checkScrollPosition() {
    if (this.isLoading || !this.hasMorePages) return;

    const scrollTop = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // USE this.triggerDistance TO CONTROL WHEN LOADING STARTS
    const triggerPoint = documentHeight - windowHeight - this.triggerDistance;
    
    if (scrollTop >= triggerPoint) {
      this.loadMore();
    }
  }

  async loadMore() {
    if (this.isLoading || !this.hasMorePages) return;

    this.isLoading = true;
    this.showLoading();

    try {
      const nextPage = this.currentPage + 1;
      const url = this.buildNextPageUrl(nextPage);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const html = await response.text();
      const newProducts = this.extractProducts(html);
      
      if (newProducts.length > 0) {
        this.appendProducts(newProducts);
        this.currentPage = nextPage;
        // URL stays the same - don't update on scroll
        this.hasMorePages = this.checkMorePagesInHTML(html);
      } else {
        this.hasMorePages = false;
      }
    } catch (error) {
      this.hasMorePages = false;
      this.showPagination();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  buildNextPageUrl(page) {
    const url = new URL(window.location);
    url.searchParams.set('page', page);
    return url.toString();
  }

  extractProducts(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const selectors = [
      '[data-product-column] > .col',
      '.collection .row > .col',
      '.product-grid .col',
      '.row > .col:has(.product-grid-item)',
      '.col:has([data-product-id])'
    ];
    
    let products = [];
    
    for (const selector of selectors) {
      products = doc.querySelectorAll(selector);
      if (products.length > 0) break;
    }
    
    return Array.from(products);
  }

  checkMorePagesInHTML(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const pagination = doc.querySelector('.pagination');
    
    if (!pagination) return false;
    
    const currentPageElement = pagination.querySelector('.pagination__item--current');
    const allPageLinks = pagination.querySelectorAll('a.pagination__item.link');
    const currentPageNum = parseInt(currentPageElement?.textContent?.trim()) || this.currentPage + 1;
    
    return Array.from(allPageLinks).some(link => {
      const pageNum = parseInt(link.textContent?.trim());
      return pageNum && pageNum > currentPageNum;
    });
  }

  appendProducts(products) {
    const fragment = document.createDocumentFragment();
    
    products.forEach((product, index) => {
      const productClone = product.cloneNode(true);
      
      if (productClone.classList.contains('scroll-trigger')) {
        const animationOrder = this.container.children.length + index + 1;
        productClone.style.setProperty('--animation-order', animationOrder);
        
        productClone.classList.remove('animate--slide-in');
        requestAnimationFrame(() => {
          productClone.classList.add('animate--slide-in');
        });
      }
      
      fragment.appendChild(productClone);
    });
    
    this.container.appendChild(fragment);
    this.reinitializeScripts();
  }

  reinitializeScripts() {
    if (window.QuickAdd) {
      const quickAddBtns = this.container.querySelectorAll('quick-add');
      quickAddBtns.forEach(btn => {
        if (!btn.initialized) {
          new window.QuickAdd(btn);
        }
      });
    }
    
    if (window.ProductForm) {
      const forms = this.container.querySelectorAll('product-form');
      forms.forEach(form => {
        if (!form.initialized) {
          new window.ProductForm(form);
        }
      });
    }
    
    document.dispatchEvent(new CustomEvent('infinite-scroll:products-loaded'));
  }

  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'block';
    }
  }

  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'none';
    }
  }

  resetScroll() {
    this.currentPage = 1;
    this.hasMorePages = true;
    this.isLoading = false;
    this.hideLoading();
    
    setTimeout(() => {
      this.checkForMorePages();
      if (this.hasMorePages) {
        this.hidePagination();
      } else {
        this.showPagination();
      }
    }, 100);
  }

  // Public methods
  triggerLoadMore() {
    this.loadMore();
  }

  disable() {
    this.hasMorePages = false;
    this.hideLoading();
    this.showPagination();
  }

  enable() {
    this.checkForMorePages();
    if (this.hasMorePages) {
      this.hidePagination();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const isCollectionPage = window.location.pathname.includes('/collections/') ||
                          document.body.classList.contains('template-collection') ||
                          document.querySelector('.collection-grid-section');
  
  if (isCollectionPage) {
    window.infiniteScroll = new InfiniteScroll();
  }
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = InfiniteScroll;
}